import type { Game } from '../out/client_web';

// The capture is intentionally opt-in and bounded. Times in this file are all
// performance.now() values from the same browser process, never server clocks.
type AckShard = {
    shard: string; last_sent_input: number; received_step: number;
    received_input: number; played_step: number; played_input: number;
    buffered_ticks: number;
    camera_entity: string | null; camera_position: [number, number, number] | null;
    run_start_step: number | null; run_settled_step: number | null;
    run_predict_step: number | null;
    terrain: { held: number; desired: number; drawn: number;
        drawn_by_lod: Record<string, number>; cut_hash: string } | null;
};
type AckSnapshot = { latest_input: number; retained_inputs: number;
    entity_bandwidth: number; entity_burst_bandwidth: number; shards: AckShard[] };
type Sample = {
    t: number; frame_gap_ms: number; render_ms: number; tick_ms: number;
    ui_ms: number; status: number; visibility: DocumentVisibilityState;
    latest_input: number; retained_inputs: number;
    shards: (AckShard & { awaiting_ack: number; unsent_inputs: number;
        oldest_unacked_ms: number | null; oldest_unsent_ms: number | null;
        received_report_age_ms: number; played_report_age_ms: number })[];
    rtc?: unknown;
};

const PRE_MS = 45_000;
const POST_MS = 10_000;
const MAX_SAMPLES = 3_600;
const TRIGGER_AGE_MS = 700;

function forwardDistance(newer: number, older: number): number {
    return (newer - older + 65_536) % 65_536;
}

export class LatencyCapture {
    readonly started = performance.now();
    readonly metadata = {
        schema: 1,
        origin: location.origin,
        browser: navigator.userAgent,
        started_iso: new Date().toISOString(),
        page_visibility: document.visibilityState,
        // Build hashes are recorded by the task runner. No URL query, access
        // token, packet payload or input value is written to this capture.
        asset_paths: ['/app.js', '/client_web_bg.wasm'],
    };
    private samples: Sample[] = [];
    private events: { t: number; kind: string; data?: unknown }[] = [];
    private generated = new Map<number, number>();
    private lastSeq: number | null = null;
    private lastFrame: number | null = null;
    private triggerAt: number | null = null;
    private triggerReason: string | null = null;
    private triggerArmed = true;
    private captures: { reason: string; trigger_at_ms: number; samples: Sample[];
        events: { t: number; kind: string; data?: unknown }[] }[] = [];
    private rtc: unknown;
    private transportSettings: { entity_bandwidth: number; entity_burst_bandwidth: number } | null = null;
    private lastCamera = new Map<string, [number, number, number]>();
    private lastTerrain = new Map<string, AckShard['terrain']>();
    private lastReports = new Map<string, { receivedStep: number; receivedAt: number;
        playedStep: number; playedAt: number }>();
    private peers: RTCPeerConnection[] = [];
    private dataChannels: RTCDataChannel[] = [];
    private counters = { sent_packets: 0, sent_bytes: 0, received_packets: 0, received_bytes: 0,
        send_errors: 0, channels: 0, buffered_bytes: 0 };
    private button: HTMLButtonElement;

    constructor() {
        const original = RTCPeerConnection.prototype.createDataChannel;
        const capture = this;
        RTCPeerConnection.prototype.createDataChannel = function (...args) {
            const channel = original.apply(this, args);
            capture.watchChannel(this, channel);
            return channel;
        };
        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.textContent = 'Save latency trace';
        Object.assign(this.button.style, {
            position: 'fixed', right: '8px', bottom: '8px', zIndex: '10000',
            font: '12px sans-serif', padding: '6px', opacity: '0.8',
        });
        this.button.onclick = () => this.download();
        document.body.appendChild(this.button);
        document.addEventListener('visibilitychange', () => this.event('visibility', document.visibilityState));
        try {
            const observer = new PerformanceObserver(list => {
                for (const entry of list.getEntries()) {
                    this.event('long_task', { start_ms: entry.startTime, duration_ms: entry.duration });
                }
            });
            observer.observe({ entryTypes: ['longtask'] });
        } catch { /* Browser does not expose Long Tasks. Frame gaps remain available. */ }
        window.setInterval(() => { void this.pollRtc(); }, 1_000);
    }

    private watchChannel(peer: RTCPeerConnection, channel: RTCDataChannel) {
        if (!this.peers.includes(peer) && this.peers.length < 4) this.peers.push(peer);
        if (this.dataChannels.length < 4) this.dataChannels.push(channel);
        this.counters.channels++;
        const send = channel.send.bind(channel);
        channel.send = (data: string | Blob | ArrayBuffer | ArrayBufferView) => {
            const bytes = typeof data === 'string' ? new TextEncoder().encode(data).length
                : data instanceof Blob ? data.size : data.byteLength;
            try {
                send(data);
                this.counters.sent_packets++;
                this.counters.sent_bytes += bytes;
                this.counters.buffered_bytes = channel.bufferedAmount;
            } catch (error) {
                this.counters.send_errors++;
                this.event('rtc_send_error', { name: (error as Error)?.name ?? 'unknown' });
                throw error;
            }
        };
        channel.addEventListener('message', event => {
            this.counters.received_packets++;
            this.counters.received_bytes += event.data instanceof ArrayBuffer ? event.data.byteLength : 0;
        });
        channel.addEventListener('close', () => this.event('rtc_close'));
    }

    private async pollRtc() {
        this.counters.buffered_bytes = this.dataChannels.reduce((sum, channel) => sum + channel.bufferedAmount, 0);
        const peers = [];
        for (const peer of this.peers) {
            if (peer.connectionState === 'closed') continue;
            try {
                const stats = await peer.getStats();
                const byId = new Map<string, any>();
                let selected: any;
                stats.forEach((value: any, id) => {
                    byId.set(id, value);
                    if (value.type === 'transport' && value.selectedCandidatePairId) selected = value;
                });
                const pair = selected && byId.get(selected.selectedCandidatePairId);
                peers.push({ state: peer.connectionState, ice: peer.iceConnectionState,
                    rtt_ms: pair?.currentRoundTripTime == null ? null : pair.currentRoundTripTime * 1_000,
                    bytes_sent: pair?.bytesSent ?? null, bytes_received: pair?.bytesReceived ?? null,
                    packets_sent: pair?.packetsSent ?? null, packets_received: pair?.packetsReceived ?? null,
                    packets_lost: pair?.packetsLost ?? null, retransmissions: pair?.retransmissions ?? null,
                    available_outbound_bps: pair?.availableOutgoingBitrate ?? null });
            } catch { peers.push({ state: peer.connectionState, stats_error: true }); }
        }
        this.rtc = { ...this.counters, peers };
    }

    private event(kind: string, data?: unknown) {
        this.events.push({ t: performance.now(), kind, data });
        if (this.events.length > 256) this.events.shift();
    }

    markAction(kind: 'primary_down' | 'primary_up') { this.event(kind); }

    frame(game: Game, time: number, renderMs: number, tickMs: number, uiMs: number) {
        let ack: AckSnapshot;
        try { ack = JSON.parse(game.input_ack_snapshot()); } catch { return; }
        this.transportSettings ??= { entity_bandwidth: ack.entity_bandwidth,
            entity_burst_bandwidth: ack.entity_burst_bandwidth };
        const frameGap = this.lastFrame === null ? 0 : time - this.lastFrame;
        this.lastFrame = time;
        if (frameGap > 200) this.event('frame_gap', { duration_ms: frameGap });
        if (this.lastSeq !== null) {
            const added = forwardDistance(ack.latest_input, this.lastSeq);
            if (added <= 240) {
                for (let i = 1; i <= added; i++) this.generated.set((this.lastSeq + i) % 65_536, time);
            } else {
                this.generated.clear();
                this.event('input_sequence_reset', { before: this.lastSeq, after: ack.latest_input });
            }
        }
        this.lastSeq = ack.latest_input;
        const shards = ack.shards.map(shard => {
            const awaiting = Math.min(240, forwardDistance(shard.last_sent_input, shard.received_input));
            const unsent = Math.min(240, forwardDistance(ack.latest_input, shard.last_sent_input));
            const first = (shard.received_input + 1) % 65_536;
            const firstUnsent = (shard.last_sent_input + 1) % 65_536;
            const position = shard.camera_position;
            const previous = this.lastCamera.get(shard.shard);
            let report = this.lastReports.get(shard.shard);
            if (!report) {
                report = { receivedStep: shard.received_step, receivedAt: time,
                    playedStep: shard.played_step, playedAt: time };
                this.lastReports.set(shard.shard, report);
            }
            if (report.receivedStep !== shard.received_step) {
                report.receivedStep = shard.received_step;
                report.receivedAt = time;
            }
            if (report.playedStep !== shard.played_step) {
                report.playedStep = shard.played_step;
                report.playedAt = time;
            }
            if (position) {
                if (previous && Math.hypot(...position.map((v, i) => v - previous[i])) > 5) {
                    this.event('camera_jump', { shard: shard.shard, from: previous, to: position });
                }
                this.lastCamera.set(shard.shard, position);
            }
            const previousTerrain = this.lastTerrain.get(shard.shard);
            if (shard.terrain && previousTerrain &&
                shard.terrain.cut_hash !== previousTerrain.cut_hash) {
                this.event('terrain_cut_change', { shard: shard.shard,
                    from: previousTerrain, to: shard.terrain });
            }
            this.lastTerrain.set(shard.shard, shard.terrain);
            return { ...shard, awaiting_ack: awaiting, unsent_inputs: unsent,
                oldest_unacked_ms: awaiting ? time - (this.generated.get(first) ?? time) : null,
                oldest_unsent_ms: unsent ? time - (this.generated.get(firstUnsent) ?? time) : null,
                received_report_age_ms: time - report.receivedAt,
                played_report_age_ms: time - report.playedAt };
        });
        const oldestNeeded = shards.reduce((oldest, shard) => Math.max(oldest, shard.awaiting_ack + shard.unsent_inputs), 0);
        for (const seq of this.generated.keys()) {
            if (forwardDistance(ack.latest_input, seq) > oldestNeeded + 2) this.generated.delete(seq);
        }
        this.samples.push({ t: time, frame_gap_ms: frameGap, render_ms: renderMs,
            tick_ms: tickMs, ui_ms: uiMs, status: game.status(),
            visibility: document.visibilityState, latest_input: ack.latest_input,
            retained_inputs: ack.retained_inputs, shards, rtc: this.rtc });
        while (this.samples.length > MAX_SAMPLES ||
            (this.triggerAt === null && this.samples[0]?.t < time - PRE_MS)) this.samples.shift();
        const aged = shards.some(s => Math.max(s.oldest_unacked_ms ?? 0, s.oldest_unsent_ms ?? 0) > TRIGGER_AGE_MS);
        if (!aged && this.triggerAt === null) this.triggerArmed = true;
        if (this.triggerAt === null && this.triggerArmed && aged) {
            this.triggerAt = time;
            this.triggerReason = 'oldest_unacked_input_over_700ms';
            this.triggerArmed = false;
            this.event('trigger', { reason: this.triggerReason });
        }
        if (this.triggerAt !== null && time - this.triggerAt >= POST_MS) {
            const start = this.triggerAt - PRE_MS;
            this.captures.push({ reason: this.triggerReason ?? 'unknown', trigger_at_ms: this.triggerAt,
                samples: this.samples.filter(sample => sample.t >= start),
                events: this.events.filter(event => event.t >= start) });
            if (this.captures.length > 3) this.captures.shift();
            this.triggerAt = null;
            this.triggerReason = null;
            this.button.textContent = `Save latency traces (${this.captures.length} events)`;
        }
    }

    snapshot() {
        return { metadata: this.metadata, transport_settings: this.transportSettings,
            trigger_at_ms: this.triggerAt,
            captures: this.captures, samples: this.samples, events: this.events };
    }

    async download() {
        // Snapshot before hashing so the click captures the moments the player
        // saw, even if a large WASM digest takes some time to complete.
        const trace = this.snapshot();
        const asset_sha256: Record<string, string | null> = {};
        for (const path of this.metadata.asset_paths) {
            try {
                const response = await fetch(path, { cache: 'force-cache' });
                if (!response.ok) throw new Error('asset unavailable');
                const bytes = await response.arrayBuffer();
                const hash = await crypto.subtle.digest('SHA-256', bytes);
                asset_sha256[path] = Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
            } catch { asset_sha256[path] = null; }
        }
        const url = URL.createObjectURL(new Blob([JSON.stringify({ ...trace, asset_sha256 })], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `aether-latency-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    }
}
